/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useTranslation } from "react-i18next";
import { MediaBatchData } from "../../../types/mediaBatchCommon.types";
import Select from "react-select";

interface Props {
	mediaBatchData: MediaBatchData | null;
  availableVolume: number;
	usedVolume: number;
	remainingVolume: number;

  batchList: any[];
  onBatchChange: (batchId: string) => void;
  onSearch: (keyword: string) => void;
}
const MDMediaBatchDetail = ({ mediaBatchData, availableVolume, usedVolume, remainingVolume, batchList, onBatchChange, onSearch}: Props) => {
	const { t } = useTranslation();
	const solutionConsumption = mediaBatchData?.solution_consumption[0];

	return (
		<div className="bg-white rounded-[1rem] shadow-2xl shadow-slate-200/50 border border-slate-200 overflow-hidden">
          <div className="px-10 py-3 border-b border-slate-100 flex items-center justify-between bg-[#0F172A] text-white">
            <h2 className="text-xs font-black uppercase tracking-[0.3em]">{t("mediaBatchInit.dispenseEntry.mediaPreparationBatch")}</h2>
          </div>
          <div className="px-10 pt-4 pb-0">
            <div className="grid grid-cols-12 gap-10 border-b border-slate-100 pb-2">
              <div className="col-span-3 space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">{t("mediaBatchInit.dispenseEntry.mediaBatchNo")} *</label>
                <div className="relative group">
                  <Select
                      className="w-[180px] text-[12px]"
                      value={
                        batchList
                          .map((batch) => ({
                            value: batch.id,
                            label: batch.media_batch_identifier,
                          }))
                          .find((o) => o.value === mediaBatchData?.id)
                      }
                      options={batchList.map((batch) => ({
                        value: batch.id,
                        label: batch.media_batch_identifier,
                      }))}
                      onInputChange={(value, { action }) => {
                        if (action === "input-change") onSearch(value);
                      }}
                      onChange={(option) => {
                        if (option) {
                          onBatchChange(String(option.value));
                        }
                      }}
                      isSearchable
                      styles={{
                      control: (base, state) => ({
                        ...base,
                        minHeight: "36px",
                        height: "36px",
                        border: state.isFocused ? "1px solid #4B5563" : "1px solid #D1D5DB",
                        borderRadius: "8px",
                        boxShadow: "none",
                        "&:hover": { border: "1px solid #4B5563" },
                      }),
                      valueContainer: (base) => ({ ...base, height: "36px", padding: "0 12px", display: "flex", alignItems: "center" }),
                      singleValue: (base) => ({ ...base, margin: 0, position: "static" }),
                      input: (base) => ({ ...base, margin: 0, padding: 0 }),
                      indicatorsContainer: (base) => ({ ...base, height: "36px" }),
                    }}
                    />
                  <div className="absolute left-[130px] top-1/2 -translate-y-1/2 pointer-events-none">
                    {/* <span className="material-symbols-outlined text-[18px] text-slate-300">
                      expand_more
                    </span> */}
                  </div>
                  <div className="absolute -bottom-6 left-0">
                    <span
                      className={`px-2 py-0.5 text-[8px] font-black uppercase rounded border
                      ${
                          mediaBatchData?.status === "Rejected"
                              ? "bg-red-50 text-red-600 border-red-100"
                              : mediaBatchData?.status === "Ready to Use"
                              ? "bg-green-50 text-green-600 border-green-100"
                              : mediaBatchData?.status === "Sterilized"
                              ? "bg-blue-50 text-blue-600 border-blue-100"
                              : mediaBatchData?.status === "Fine Tuned"
                              ? "bg-purple-50 text-purple-600 border-purple-100"
                              : "bg-emerald-50 text-emerald-600 border-emerald-100"
                      }`}
                  >
                      {mediaBatchData?.status}
                  </span>
                  </div>
                </div>
              </div>

              <div className="col-span-9 grid grid-cols-4 gap-8">
                 {[
                   { label: t("mediaBatchInit.dispenseEntry.mediaName"), val: mediaBatchData?.intended_use_name },
                   { label: t("mediaBatchInit.dispenseEntry.mediaType"), val: mediaBatchData?.media_type },
                   { label: t("mediaBatchInit.dispenseEntry.preparationDate"), val: mediaBatchData?.created_at },
                   { label: t("mediaBatchInit.dispenseEntry.preparedBy"), val: mediaBatchData?.lead_technician_name }
                 ].map((f, i) => (
                   <div key={i} className="space-y-1 pt-1">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">{f.label}</p>
                      <p className="text-[12px] font-black text-[#1E293B]">{f.val}</p>
                   </div>
                 ))}
              </div>
            </div>

            <div className="grid grid-cols-12 gap-10 py-10">
               <div className="col-span-3 flex items-center gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 shadow-inner">
                    <span className="material-symbols-outlined text-2xl font-black">science</span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mediaBatchInit.dispenseEntry.availableVolume")}</p>
							<p className="text-[12px] font-black text-[#0F172A]">{availableVolume.toFixed(3)}</p>
                  </div>
               </div>
               <div className="col-span-9 grid grid-cols-3 gap-8">
                  {[
							      { label: t("mediaBatchInit.dispenseEntry.usedVolume"), val: usedVolume, icon: 'sync_alt' },
                    { label: t("mediaBatchInit.dispenseEntry.remainingVolume"), val: remainingVolume , icon: 'equalizer', highlight: 'text-blue-600' },
                    
                    // { label: t("mediaBatchInit.dispenseEntry.batchExpiry"), val: (
                    //   <div className="flex flex-col">
                    //     <span className="text-sm font-black"></span>
                    //     <span className="text-[9px] font-black text-orange-500 uppercase tracking-widest"></span>
                    //   </div>
                    // ), icon: 'calendar_today' },
                    // { label: t("mediaBatchInit.dispenseEntry.status"), val: <span className="text-emerald-500 font-extrabold uppercase tracking-widest"></span>, icon: 'verified_user', action: (
                    //   <button
                    //     // onClick={() => onViewBatchDetails(selectedBatch)}
                    //     className="mt-2 px-4 py-1 border border-slate-200 text-blue-600 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all font-inter"
                    //   >
                    //     {t("mediaBatchInit.dispenseEntry.viewBatchDetails")}
                    //   </button>
                    // ) }

                  ].map((field, i) => (
                    <div key={i} className="flex gap-4">
                       <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-300">
                          <span className="material-symbols-outlined text-xl">{field.icon}</span>
                       </div>
                       <div className="space-y-1">
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{field.label}</p>
                          <div className={`text-[12px] font-black ${field.highlight || "text-[#1E293B]"}`}>
                            {typeof field.val === "number" ? field.val.toFixed(3) : field.val}
                          </div>
                          {/*{field.action}*/}
                       </div>
                    </div>
                  ))}
               </div>
            </div>
          </div>
          {/* Dark Ribbon */}
          <div className="bg-[#1E293B] px-10 py-3 flex items-center gap-6">
            <span className="material-symbols-outlined text-slate-500">list_alt</span>
            <div className="flex items-center gap-4 text-[10px] font-black tracking-widest uppercase">
               <span className="text-blue-400 font-black">{t("mediaBatchInit.dispenseEntry.mediaBatchContext")}</span>
               <span className="text-slate-500">|</span>
					     <span className="text-slate-200">{mediaBatchData?.media_batch_identifier}</span>
               <span className="text-slate-500">|</span>
					     <span className="text-slate-200">{mediaBatchData?.media_type}</span>
               <span className="text-slate-500">|</span>
               <span className="text-slate-200">{solutionConsumption?.solution_remaining_qty} ml Available</span>
               {/*<span className="text-slate-500">|</span>
               <span className="text-emerald-400">QA Released</span>*/}
               {/*<span className="text-slate-500">|</span>*/}
               {/*<span className="text-slate-200">Expires on 08 Feb 2026</span>
               <span className="text-orange-400">(6 Days Left)</span>*/}
            </div>
          </div>
        </div>
	);
};

export default MDMediaBatchDetail;
